import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { Dialog, DialogProps, ElevatedButton, TextButton } from '@producer/ui-kit';

import { breakPoint } from '../../../app/theme';
import { Project, Shot, Space } from '../../../app/entities';
// import { ImageRadioButton } from '../../../components/image-radio-button/image-radio-button';
import { PillsSelector } from '../../../components/pills-selector/pills-selector';
import { TextAreaInput } from '../../../components/textarea-input/TextAreaInput';
import { uiStore } from '../../../core/stores/ui-store';
import { observer } from 'mobx-react-lite';

type GenerateShotCoverDto = {
  style: 'cinematic' | 'pencil' | 'cartoon';
  additionalPrompt?: string;
  promptPropsIds?: string[];
};

type GenerateAiCoverImageProps = DialogProps & {
  shot: Shot;
  space: Space;
};

const propKeysToExclude = ['order', 'cover'];
const preSelectedProps = [
  'description',
  'charactersIds',
  'locationId',
  'shotSize',
  'angle',
  'lens',
  'timing',
];

const styleOptions = [
  {
    label: 'Cinematic',
    value: 'cinematic',
    image: '/images/ai-image-styles/cinematic.jpg',
  },
  {
    label: 'Pencil',
    value: 'pencil',
    image: '/images/ai-image-styles/pencil.jpg',
  },
  {
    label: 'Cartoon 3D',
    value: 'cartoon',
    image: '/images/ai-image-styles/cartoon.jpg',
  },
];

export const GenerateAiCoverImage: React.FC<GenerateAiCoverImageProps> = observer(
  ({ onCancel, shot, space }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [propsOptions, setPropsOptions] = useState([]);

    const generateSchema = yup.object().shape({
      style: yup.mixed().oneOf(['cinematic', 'pencil', 'cartoon'], 'Invalid style').required(),
      additionalPrompt: yup.string().max(1000, 'Max 1000 characters').optional(),
      promptPropsIds: yup.array().of(yup.string()).optional(),
    });

    const { handleSubmit, control, setValue } = useForm<GenerateShotCoverDto>({
      mode: 'onSubmit',
      resolver: yupResolver(generateSchema),
      defaultValues: {
        style: 'cinematic',
        additionalPrompt: '',
        promptPropsIds: [],
      },
    });

    useEffect(() => {
      const project = Project.getOne(shot.projectId);

      if (project) {
        const options = [];
        const selectedProps = [];

        for (const prop of project.properties) {
          const isPreselected = preSelectedProps.includes(prop.key);
          if (isPreselected) {
            selectedProps.push(prop._id);
          }

          if (!propKeysToExclude.includes(prop.key)) {
            options.push({ label: prop.name, value: prop._id });
          }
        }

        setPropsOptions(options);
        if (selectedProps.length) {
          setValue('promptPropsIds', selectedProps);
        }
      }
    }, []);

    const onSubmit = async (input: GenerateShotCoverDto) => {
      if (isLoading) {
        return;
      }

      if (space.credits?.current < 5) {
        uiStore.openInsufficientPanel();
        return;
      }

      setIsLoading(true);

      for (const key in input) {
        if (input[key] === '') delete input[key];
      }

      space.decrementCredits(5);

      await shot.generateAiImage(input);

      onCancel();
      setIsLoading(false);
    };

    return (
      <Dialog
        title="Generate AI Image"
        onClose={onCancel}
        actions={[
          <TextButton key={'cancel'} onClick={onCancel} text={'Cancel'} />,
          <ElevatedButton
            key="generate"
            text="Generate"
            onClick={handleSubmit(onSubmit)}
            isLoading={isLoading}
          />,
        ]}
      >
        <Content>
          {/**<StyleSelectionWrapper>
            <Header>Style</Header>

            <ImageRadioButton name="style" control={control} options={styleOptions} />
          </StyleSelectionWrapper>
           **/}

          <PropsSelectionWrapper>
            <Header>Consider these properties for image generation</Header>

            <PillsSelector
              required
              name="promptPropsIds"
              control={control}
              options={propsOptions}
            />
          </PropsSelectionWrapper>

          <Controller
            name="additionalPrompt"
            control={control}
            render={({ field: { onChange }, fieldState: { error } }) => (
              <TextAreaInput
                errorMessage={error?.message}
                placeholder="Additional AI Prompt: Specify the image generation"
                onChange={onChange}
              />
            )}
          />
        </Content>
      </Dialog>
    );
  },
);

const Content = styled.div`
  max-width: 48rem;
  flex: 1;
  overflow-y: scroll;

  & textarea {
    color: #959bab;
    font-weight: 400;
    font-size: 1.4rem;
  }

  @media screen and (min-width: ${breakPoint.small}px) {
    width: 47.5rem;
    max-width: 100%;
  }
`;

const StyleSelectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.p`
  color: var(--color-grayscale-white);
  font-weight: 400;
  font-size: 1.4rem;
  margin-bottom: 1.6rem;
`;

const PropsSelectionWrapper = styled.div``;

const DashedInput = css`
  display: flex;
  box-sizing: border-box;
  background-color: transparent;
  border-radius: 0.4rem;
  border: 0.1rem var(--color-texts-low-contrast) dashed;
  font-size: 1.4rem;
  height: 3.2rem;
  min-height: 3.2rem;
  font-weight: 400;
  overflow: hidden;

  &:hover {
    border: 0.1rem var(--color-texts-middle-contrast) dashed;
    background-color: var(--color-surfaces-bg-elevation-3);

    & div {
      color: var(--color-texts-low-contrast);
    }
  }

  &[data-disabled='true'] {
    background: transparent;
    border-color: var(--color-texts-low-contrast);
  }
`;

export const DateSelectorWrapper = styled.div`
  ${DashedInput}

  width: fit-content !important;

  & > div {
    padding: 0.6rem 0.8rem;
    font-size: 1.4rem;
    color: var(--color-texts-low-contrast);

    &:hover {
      background-color: var(--color-surfaces-bg-elevation-3);
    }
  }

  &[data-borderless='true'] {
    border-color: transparent;
    & > div {
      color: var(--color-texts-high-contrast);
    }
  }
`;
